import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import {
  CommissioningDeviceAssignRoomParams,
  CommissioningStore,
  DeviceAssignmentType,
  InstallationFeaturesStore,
  RoomTypes,
} from '@store';
import {
  DeviceHeatingCircuitIdCommands,
  DeviceIds,
  Feature,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  IsEnabledProperty,
  IsNumberValueProperty,
  RoomCommands,
  RoomProperties,
} from '@vi-iot/client';
import { Subject, of, zip } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { noWhitespaceValidator, uniqueNameValidator } from '../../../../../shared/common';
import { NoSpecialCharactersValidator } from '../../../../../shared/validators/special-characters/special-characters.validator';
import { ModalAction } from '../commissioning.model';
import { CommissioningService } from '../commissioning.service';

export enum AddRoomOptionType {
  Room,
  NewRoom,
}

export interface RadioOption {
  feature: string;
  type: AddRoomOptionType;
  name: string;
}
@Component({
  templateUrl: './commissioning-add-room-modal.component.html',
  styleUrls: ['./commissioning-add-room-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissioningAddRoomModalComponent implements OnInit, OnDestroy {
  public readonly NEW_ROOM_OPTION_NAME =
    '+SRC_COMMISSIONINGV2.ADD_ROOM_EMBEDED_ADD_NEW_ROOM_OPTION';
  AddRoomOptionType = AddRoomOptionType;

  // Room Radio Group
  radioOptions: RadioOption[];
  selectedOption: RadioOption;

  // Add New Room Forms
  addNewRoomFormGroup: FormGroup<{
    name: FormControl<string>;
    type: FormControl<string>;
  }>;
  roomTypeOptions: string[];
  private cachedAddedRoomName: string;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public commissioningService: CommissioningService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<CommissioningAddRoomModalComponent>,
    private commissioningStore: CommissioningStore,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.dialogRef.updateSize('768px');
    this.dialogRef.addPanelClass('commissioning-modal');

    this.roomTypeOptions = Object.keys(RoomTypes);

    this.installationFeaturesStore.rooms$
      .pipe(
        map((rooms) => {
          return rooms.filter((entry) => {
            return !!entry?.properties?.name?.value;
          });
        }),
        distinctUntilChanged((prev, curr) => prev.length === curr.length),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((rooms: Feature<RoomProperties, RoomCommands>[]) => {
        this.constructRoomsOptions(rooms);
        this.constructFormGroup();

        setTimeout(() => {
          this.cdr.markForCheck();
        }, 0);
      });
  }

  addRoom(): void {
    const roomName = this.addNewRoomFormGroup.controls.name.value.trim();
    const roomType = RoomTypes[this.addNewRoomFormGroup.controls.type.value.trim()];

    this.installationFeaturesStore.updateFeature(
      FeatureNames.rooms,
      [roomName, roomType],
      null,
      [FeaturePropertyKey.name, FeaturePropertyKey.type],
      FeatureCommands.add,
      null,
      DeviceIds.RoomControl,
      {
        hideSuccessSnackbar: true,
      }
    );

    this.cachedAddedRoomName = roomName;
  }

  onRoomOptionSelect(roomOption: RadioOption): void {
    this.selectedOption = roomOption;

    setTimeout(() => {
      this.cdr.markForCheck();
    }, 0);
  }

  closeDialog(): void {
    this.commissioningService.modalAction$.next(ModalAction.Close);
  }

  navigateBack(): void {
    this.commissioningService.modalAction$.next(ModalAction.PreviousStep);
  }

  navigateForward(): void {
    this.commissioningStore.pairedDeviceId$
      .pipe(
        take(1),
        switchMap((pairedDeviceId: string) => {
          return zip(
            this.installationFeaturesStore.selectInstallationDeviceFeature<
              IsNumberValueProperty,
              DeviceHeatingCircuitIdCommands
            >(FeatureNames.deviceHeatingCircuitId, pairedDeviceId),
            this.installationFeaturesStore.selectInstallationDeviceFeature<IsEnabledProperty, {}>(
              FeatureNames.circuits
            ),
            of(pairedDeviceId)
          ).pipe(take(1));
        })
      )
      .subscribe(([deviceHeatingCircuit, enabledHc, pairedDeviceId]) => {
        const firstEnabledHC = enabledHc?.properties?.enabled?.value[0];

        const deviceHc = deviceHeatingCircuit?.properties?.value?.value;
        const hcToAssign = deviceHc != null ? deviceHc : Number(firstEnabledHC);

        const assignedRoom = {
          type: DeviceAssignmentType.Room,
          deviceId: pairedDeviceId,
          roomFeature: this.selectedOption.feature,
          roomName: this.selectedOption.name,
        } as CommissioningDeviceAssignRoomParams;

        if (isFinite(hcToAssign) && hcToAssign != null) {
          assignedRoom.heatingCircuitId = hcToAssign;
        }

        this.commissioningStore.deviceAssignRoom(assignedRoom);
        this.commissioningService.modalAction$.next(ModalAction.NextStep);
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private constructRoomsOptions(rooms: Feature<RoomProperties, RoomCommands>[]): void {
    this.radioOptions = rooms.map((entry) => {
      return {
        feature: entry.feature,
        type: AddRoomOptionType.Room,
        name: entry?.properties?.name?.value,
      };
    });

    this.radioOptions.push({
      feature: null,
      type: AddRoomOptionType.NewRoom,
      name: this.NEW_ROOM_OPTION_NAME,
    });

    const cachedRoom = this.radioOptions.find((entry) => entry.name === this.cachedAddedRoomName);
    this.selectedOption = !!cachedRoom ? cachedRoom : this.radioOptions[0];
  }

  private constructFormGroup(): void {
    this.addNewRoomFormGroup = this.formBuilder.group({
      name: new FormControl('', [
        Validators.required,
        noWhitespaceValidator(),
        Validators.maxLength(40),
        uniqueNameValidator(this.radioOptions.map((entry) => entry.name)),
        NoSpecialCharactersValidator(),
      ]),
      type: new FormControl(this.roomTypeOptions[0]),
    });
  }
}
